import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { protocolsI18n } from './i18n';

const getGroupSortValue = (row, key) => {
  switch (key) {
    case 'name':
      return row.name || '';
    case 'email':
      return row.email || '';
    default:
      return '';
  }
};

const NewAnalysisModal = ({
  Modal,
  Button,
  ComboBox,
  Input,
  Table,
  show,
  onClose,
  modalStep,
  onConfirm,
  onBack,
  onAnalyze,
  isAnalyzing,
  canAnalyze,
  protocolOptions,
  selectedProtocol,
  onSelectProtocol,
  protocolSteps,
  agentOptions,
  selectedAgent,
  onSelectAgent,
  selectedDate,
  onSelectedDateChange,
  callListRows,
  selectedCallIds,
  onToggleCallId,
  onToggleCallAll,
  selectedCallsCount,
  isLoadingCallsCount,
  formatDate,
  formatDateTime,
  analysisType,
  analysisTypeOptions,
  onAnalysisTypeChange,
  groupRows,
  selectedGroupAgents,
  onToggleGroupAgent,
  onToggleGroupAll,
}) => {
  const { t } = useTranslation(undefined, { i18n: protocolsI18n });

  const areaOptions = useMemo(
    () => [
      { label: t('pr_modal_area_all'), value: 'all' },
      { label: t('pr_modal_area_ventas'), value: 'ventas' },
      { label: t('pr_modal_area_sac'), value: 'sac' },
    ],
    [t]
  );

  const isConfirmStep = modalStep === 'confirm';
  const isGroup = analysisType?.value === 'group';
  const headerCheckboxRef = useRef(null);
  const callHeaderCheckboxRef = useRef(null);
  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [groupSearch, setGroupSearch] = useState('');
  const [groupArea, setGroupArea] = useState(null);
  const effectiveGroupArea = groupArea ?? areaOptions[0];
  const typeOptions = analysisTypeOptions || [];
  const calls = Array.isArray(callListRows) ? callListRows : [];

  const handleAnalysisTypeChange = (option) => {
    onAnalysisTypeChange?.(option);
  };

  const filteredGroupRows = useMemo(() => {
    const query = groupSearch.trim().toLowerCase();
    const areaValue = (effectiveGroupArea?.value || 'all').toLowerCase();

    return groupRows.filter((row) => {
      const name = `${row.name || ''}`.toLowerCase();
      const email = `${row.email || ''}`.toLowerCase();
      const matchesQuery =
        !query || name.includes(query) || email.includes(query);

      if (!matchesQuery) return false;
      if (areaValue === 'all') return true;

      const areaSource =
        row.area || row.areaName || row.department || row.operacion || '';
      const rowArea = `${areaSource}`.toLowerCase();
      if (!rowArea) return true;
      return rowArea.includes(areaValue);
    });
  }, [groupRows, groupSearch, effectiveGroupArea]);

  const groupIds = filteredGroupRows.map((row) => row.id);
  const allSelected =
    groupIds.length > 0 &&
    groupIds.every((id) => selectedGroupAgents.includes(id));
  const someSelected =
    groupIds.some((id) => selectedGroupAgents.includes(id)) && !allSelected;

  const callIds = calls
    .map((call) => call.callId)
    .filter((id) => id !== null && id !== undefined);
  const allCallsSelected =
    callIds.length > 0 && callIds.every((id) => selectedCallIds?.includes(id));
  const someCallsSelected =
    callIds.some((id) => selectedCallIds?.includes(id)) && !allCallsSelected;

  useEffect(() => {
    if (!headerCheckboxRef.current) return;
    headerCheckboxRef.current.indeterminate = someSelected;
  }, [someSelected]);

  useEffect(() => {
    if (!callHeaderCheckboxRef.current) return;
    callHeaderCheckboxRef.current.indeterminate = someCallsSelected;
  }, [someCallsSelected]);

  useEffect(() => {
    if (!show) {
      setSortKey(null);
      setSortDirection(null);
    }
  }, [show]);

  const sortedGroupRows = useMemo(() => {
    const data = [...filteredGroupRows];
    if (!sortKey || !sortDirection) return data;
    const direction = sortDirection === 'desc' ? -1 : 1;
    data.sort((a, b) => {
      const aValue = getGroupSortValue(a, sortKey);
      const bValue = getGroupSortValue(b, sortKey);
      return (
        aValue.localeCompare(bValue, 'es', {
          sensitivity: 'base',
          numeric: true,
        }) * direction
      );
    });
    return data;
  }, [filteredGroupRows, sortKey, sortDirection]);

  const handleGroupSortChange = ({ sortKey: key, direction }) => {
    const normalized =
      typeof direction === 'string' ? direction.toLowerCase() : direction;
    setSortKey(normalized ? key : null);
    setSortDirection(normalized ?? null);
  };

  const renderProtocolSteps = () => {
    if (!selectedProtocol) return null;
    if (!protocolSteps.length) {
      return (
        <div className='color-gray-600 small'>
          {t('pr_modal_no_steps')}
        </div>
      );
    }

    const renderStep = (step, indexForOrder, key) => {
      const order =
        step.order ??
        step.numeroPaso ??
        step.stepNumber ??
        step.stepOrder ??
        indexForOrder + 1;

      const text =
        step.stepName ||
        step.stepDescription ||
        step.descripcion ||
        step.description ||
        step.name ||
        step.detalle ||
        step.text ||
        t('pr_modal_step_unnamed');

      return (
        <li key={key} value={order} className='caption mb-2 step-list'>
          {text}
        </li>
      );
    };

    return (
      <ol className='mt-3 ps-3'>
        {protocolSteps.map((step, index) =>
          renderStep(step, index, step.id ?? index)
        )}
      </ol>
    );
  };

  if (!Modal) return null;

  return (
    <Modal
      title={t('pr_modal_title')}
      show={show}
      onClose={onClose}
      primaryBtnTxt={isConfirmStep ? t('pr_modal_confirm_btn') : null}
      primaryBtnAction={isConfirmStep ? onAnalyze : null}
      disabledPrimaryBtn={isConfirmStep ? isAnalyzing : false}
      secondaryBtnTxt={isConfirmStep ? t('pr_modal_cancel_btn') : null}
      secondaryBtnAction={isConfirmStep ? onBack : null}
      ctaAligment='right'
      width={isConfirmStep ? '560px' : '80%'}
      height={isConfirmStep ? '320px' : undefined}
    >
      <div>
        {isConfirmStep ? (
          <div className='d-flex flex-column gap-3'>
            <p className='mb-0'>
              {t('pr_modal_confirm_text', {
                count: selectedCallIds?.length || 0,
                agent: selectedAgent?.label || '',
                protocol: selectedProtocol?.label || '',
                date: formatDate(selectedDate),
              })}
            </p>
            <p className='mb-0'>
              {t('pr_modal_confirm_warning')}
            </p>
          </div>
        ) : (
          <>
            <div className='row g-3'>
              <div className='col-12 col-lg-6'>
                <div className='row g-3'>
                  <div className='col-12'>
                    <h3 className='h6 mb-2'>{t('pr_modal_select_protocol')}</h3>
                    <ComboBox
                      id='protocol-selector'
                      label={t('pr_modal_protocol_label')}
                      placeholder={t('pr_modal_protocol_placeholder')}
                      options={protocolOptions}
                      value={selectedProtocol}
                      onChange={onSelectProtocol}
                    />
                    {selectedProtocol && (
                      <div>
                        {selectedProtocol.businessLineName && (
                          <div>
                            <p className='caption fw-semibold mb-1'>
                              {t('pr_modal_business_line')}{' '}
                              <span className='badge bg-primary-subtle text-primary small mt-2'>
                                {selectedProtocol.businessLineName}
                              </span>
                            </p>

                            <p className='caption mb-1'>
                              <span className='fw-semibold font-dark'>
                                {t('pr_modal_protocol_steps')}{' '}
                              </span>
                              {protocolSteps.length}
                            </p>
                          </div>
                        )}
                        <div className='mt-2 step-parent'>
                          <div className='step-container'>
                            <div>{renderProtocolSteps()}</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className='col-12'>
                    <h3 className='h6 mb-2'>{t('pr_modal_analysis_type')}</h3>
                    {ComboBox ? (
                      <ComboBox
                        id='analysis-type-selector'
                        label={t('pr_modal_analysis_type')}
                        placeholder={t('pr_modal_type_placeholder')}
                        options={typeOptions}
                        value={analysisType}
                        onChange={handleAnalysisTypeChange}
                      />
                    ) : (
                      <select
                        className='form-select'
                        value={analysisType?.value ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          const option =
                            typeOptions.find((item) => item.value === value) ||
                            null;
                          handleAnalysisTypeChange(option);
                        }}
                      >
                        <option value=''>{t('pr_modal_type_placeholder')}</option>
                        {typeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div className='d-flex justify-content-between'>
                    {!isGroup && (
                      <div className='col-12 col-md-6'>
                        {ComboBox ? (
                          <ComboBox
                            id='agent-selector'
                            label={t('pr_col_agent')}
                            placeholder={t('pr_modal_agent_placeholder')}
                            options={agentOptions}
                            value={selectedAgent}
                            onChange={onSelectAgent}
                          />
                        ) : (
                          <select
                            className='form-select'
                            value={selectedAgent?.value ?? ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              const option =
                                agentOptions.find(
                                  (item) => `${item.value}` === value
                                ) || null;
                              onSelectAgent(option);
                            }}
                          >
                            <option value=''>{t('pr_modal_agent_placeholder')}</option>
                            {agentOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    )}

                    <div className={isGroup ? 'col-12' : 'col-12 col-md-6'}>
                      {Input ? (
                        <Input
                          label={t('pr_modal_date_label')}
                          name='analysisDate'
                          type='date'
                          value={selectedDate || ''}
                          onChange={(e) =>
                            onSelectedDateChange?.(e.target.value)
                          }
                        />
                      ) : (
                        <input
                          className='form-control'
                          type='date'
                          value={selectedDate || ''}
                          onChange={(e) =>
                            onSelectedDateChange?.(e.target.value)
                          }
                        />
                      )}
                    </div>
                  </div>
                  <div>
                    {Button ? (
                      <Button
                        label={isAnalyzing ? t('pr_modal_btn_analyzing') : t('pr_modal_btn_analyze')}
                        variant='primary'
                        size='sm'
                        onClick={onConfirm}
                        disabled={isGroup ? false : !canAnalyze}
                      />
                    ) : (
                      <button
                        type='button'
                        className='btn btn-primary btn-sm'
                        onClick={onConfirm}
                        disabled={isGroup ? false : !canAnalyze}
                      >
                        {isAnalyzing ? t('pr_modal_btn_analyzing') : t('pr_modal_btn_analyze')}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {isGroup ? (
                <div className='col-12 col-lg-6'>
                  <div className='d-flex flex-wrap gap-2 mb-3'>
                    <div className='flex-grow-1'>
                      {Input ? (
                        <Input
                          icon='search'
                          name='searchAgents'
                          onChange={(e) => setGroupSearch(e.target.value)}
                          placeholder={t('pr_modal_group_search_placeholder')}
                          type='text'
                          value={groupSearch}
                        />
                      ) : (
                        <input
                          className='form-control'
                          placeholder={t('pr_modal_group_search_placeholder')}
                          type='text'
                          value={groupSearch}
                          onChange={(e) => setGroupSearch(e.target.value)}
                        />
                      )}
                    </div>
                    <div className='flex-grow-1'>
                      {ComboBox ? (
                        <ComboBox
                          id='group-area-filter'
                          label={t('pr_modal_area_label')}
                          placeholder={t('pr_modal_area_placeholder')}
                          options={areaOptions}
                          value={effectiveGroupArea}
                          onChange={(option) => setGroupArea(option)}
                        />
                      ) : (
                        <select
                          className='form-select'
                          value={effectiveGroupArea?.value ?? 'all'}
                          onChange={(e) => {
                            const value = e.target.value;
                            const option =
                              areaOptions.find(
                                (item) => item.value === value
                              ) || areaOptions[0];
                            setGroupArea(option);
                          }}
                        >
                          {areaOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                  {Table ? (
                    <>
                      <div className='protocol-modal__table-results'>
                        <div className='table-container'>
                          <Table
                            columns={[
                              {
                                label: (
                                  <input
                                    ref={headerCheckboxRef}
                                    type='checkbox'
                                    aria-label={t('pr_modal_select_all_agents')}
                                    checked={allSelected}
                                    onChange={() =>
                                      onToggleGroupAll?.(groupIds)
                                    }
                                    disabled={groupIds.length === 0}
                                  />
                                ),
                                width: '48px',
                                render: (row) => (
                                  <input
                                    type='checkbox'
                                    aria-label={t('pr_modal_select_agent', { name: row.name })}
                                    checked={selectedGroupAgents.includes(
                                      row.id
                                    )}
                                    onChange={() => onToggleGroupAgent(row.id)}
                                  />
                                ),
                              },
                              { accessor: 'name', label: t('pr_modal_col_name'), sort: true },
                              { accessor: 'email', label: t('pr_modal_col_email'), sort: true },
                            ]}
                            data={sortedGroupRows}
                            serverSortKey={sortKey}
                            serverSortDirection={sortDirection}
                            onServerSortChange={handleGroupSortChange}
                          />
                        </div>
                      </div>
                      <p className='color-gray-600 small mt-2 mb-0'>
                        {t('pr_modal_agents_selected', { count: selectedGroupAgents.length })}
                      </p>
                    </>
                  ) : (
                    <p className='color-gray-600 small mb-0'>
                      {t('pr_modal_table_unavailable_agents')}
                    </p>
                  )}
                </div>
              ) : (
                <div className='col-12 col-lg-6'>
                  <h3 className='h6 mb-2'>{t('pr_modal_calls_title')}</h3>
                  {Table ? (
                    <div className='protocol-modal__table-results'>
                      <div className='table-container'>
                        <Table
                          columns={[
                            {
                              label: (
                                <input
                                  ref={callHeaderCheckboxRef}
                                  type='checkbox'
                                  aria-label={t('pr_modal_select_all_calls')}
                                  checked={allCallsSelected}
                                  onChange={() => onToggleCallAll?.(callIds)}
                                  disabled={callIds.length === 0}
                                />
                              ),
                              width: '48px',
                              render: (row) => (
                                <input
                                  type='checkbox'
                                  aria-label={t('pr_modal_select_call', { id: row.callId })}
                                  checked={selectedCallIds?.includes(
                                    row.callId
                                  )}
                                  onChange={() => onToggleCallId?.(row.callId)}
                                />
                              ),
                            },
                            { accessor: 'callId', label: t('pr_modal_col_call_id') },
                            { accessor: 'feeling', label: t('pr_modal_col_feeling') },
                            {
                              accessor: 'date',
                              label: t('pr_modal_col_date'),
                              render: (row) =>
                                formatDateTime
                                  ? formatDateTime(row.date)
                                  : row.date || 'N/A',
                            },
                          ]}
                          data={calls}
                          loading={isLoadingCallsCount}
                        />
                        {!isGroup && selectedAgent && selectedDate && (
                          <p className='color-gray-600 small mt-3 mb-0'>
                            {isLoadingCallsCount
                              ? t('pr_modal_calls_calculating')
                              : selectedCallIds?.length > 0
                                ? t('pr_modal_calls_selected', {
                                    selected: selectedCallIds.length,
                                    total: selectedCallsCount,
                                  })
                                : selectedCallsCount > 0
                                  ? t('pr_modal_calls_available', { total: selectedCallsCount })
                                  : t('pr_modal_calls_none')}
                          </p>
                        )}
                        {!isGroup && (selectedCallIds?.length || 0) > 3 && (
                          <p className='color-gray-600 small mt-2 mb-0 message-alert'>
                            {t('pr_modal_calls_alert')}
                          </p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className='color-gray-600 small mb-0'>
                      {t('pr_modal_table_unavailable_calls')}
                    </p>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default NewAnalysisModal;
